import { useCallback } from "react";

import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectIsPublishedAudioEnabled,
  selectIsVirtualUserPresentInClass,
  selectParticipantBarVisibleClassUserIds,
} from "store/selectors/pubsub-virtual-class-state";
import { useAvPubsubMessaging } from "utils/av";

export const usePinParticipantTools = (classUserId = "") => {
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const { canAuthorSection } = useCapabilities();
  const isVirtualUserPresentInClass = useAppSelector((state) =>
    selectIsVirtualUserPresentInClass(state, classUserId ?? ""),
  );
  const participantBarVisibleClassUserIds = useAppSelector(selectParticipantBarVisibleClassUserIds);

  const onPinClassUserIdList = useCallback(
    (newClassUserIds: string[]) => {
      if (!canAuthorSection || newClassUserIds.length === 0) return;

      sendPubsubVirtualClassStateMessage({
        action: "pin-class-users",
        class_user_ids: newClassUserIds,
      });
    },
    [canAuthorSection, sendPubsubVirtualClassStateMessage],
  );

  const isParticipantVisibleInBar =
    classUserId != null && participantBarVisibleClassUserIds.includes(classUserId);

  const pinParticipantDisabledText = !isVirtualUserPresentInClass
    ? t("user_options_menu.view_participant_not_available")
    : isParticipantVisibleInBar
      ? t("user_options_menu.view_participant_already_visible")
      : undefined;

  return { onPinClassUserIdList, pinParticipantDisabledText };
};

export const useMuteParticipantTools = (userId = "", classUserId = "") => {
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const { canAuthorSection } = useCapabilities();
  const isVirtualUserPresentInClass = useAppSelector((state) =>
    selectIsVirtualUserPresentInClass(state, classUserId ?? ""),
  );
  const isAudioEnabled = useAppSelector((state) =>
    selectIsPublishedAudioEnabled(state, classUserId ?? ""),
  );
  const onMuteParticipant = useCallback(() => {
    if (!isAudioEnabled || !canAuthorSection || !classUserId || !userId) return;
    sendPubsubVirtualClassStateMessage({
      action: "mute-users",
      user_ids: [userId],
    });
  }, [isAudioEnabled, canAuthorSection, classUserId, userId, sendPubsubVirtualClassStateMessage]);
  const muteParticipantDisabledText = !isVirtualUserPresentInClass
    ? t("user_options_menu.mute_participant_not_available")
    : !isAudioEnabled
      ? t("user_options_menu.mute_participant_already_muted")
      : undefined;
  return { onMuteParticipant, muteParticipantDisabledText };
};
